// ============================================================
// 📋 FORMULÁRIOS AUXILIARES DO SUS (B211)
// ============================================================
// Mapa DECLARATIVO de gatilhos: qual exame (por código TUSS) exige
// qual formulário auxiliar do SUS. Tabela única, testável (espírito B201).
//
// Para adicionar um novo gatilho (ex.: carga viral de HBV/HCV/HIV → seu
// formulário, imagem → APAC, imunossupressor → LME), basta acrescentar uma
// entrada em GATILHOS_FORMULARIOS — nenhuma lógica de componente muda.

export type TipoFormularioAuxiliar =
  | "gal" // CMV — Sistema GAL
  | "cargaViral" // HBV/HCV/HIV — modelo gov.br/aids
  | "apac"
  | "lme"
  | "hepatiteB";

export interface FormularioAuxiliar {
  id: string; // identificador estável do formulário
  tipo: TipoFormularioAuxiliar; // família do documento
  nome: string; // rótulo amigável exibido ao usuário
  descricao: string; // descrição/identificação oficial do documento
  codigoSUS?: string; // código do procedimento SUS, quando aplicável
  agravoDoenca?: string; // campo 39 da GAL — preenchido automaticamente
}

export interface GatilhoFormulario {
  tuss: string; // código TUSS do exame que dispara o formulário
  formulario: FormularioAuxiliar;
}

// ------------------------------------------------------------
// GATILHOS ATIVOS
// ------------------------------------------------------------
// 1º formulário (AI_95): ficha GAL para carga viral de CMV.
// 2º formulário (AI_95): carga viral de Hepatite B (modelo gov.br/aids) — o
// mesmo molde servirá HCV/HIV (só muda a config em gerarFichaCargaViral.ts).
export const GATILHOS_FORMULARIOS: GatilhoFormulario[] = [
  {
    tuss: "40314030", // CMV PCR quantitativo (catálogo examesTUSS)
    formulario: {
      id: "gal-cmv",
      tipo: "gal",
      nome: "Ficha GAL — Carga Viral de CMV",
      descricao: "Requisição de Exame – Biologia Médica (Sistema GAL)",
      codigoSUS: "02.13.01.011-9", // Carga Viral / PCR Quantitativo
      agravoDoenca: "Infecção pelo Citomegalovírus (CMV)",
    },
  },
  {
    tuss: "40314081", // HBV-DNA PCR quantitativo (catálogo examesTUSS)
    formulario: {
      id: "cv-hbv",
      tipo: "cargaViral",
      nome: "Formulário de Carga Viral — Hepatite B",
      descricao:
        "Solicitação de Exame de Carga Viral do Vírus da Hepatite B (gov.br/aids)",
      codigoSUS: "02.13.01.020-8", // Identificação do HBV por PCR (quantitativo)
      agravoDoenca: "Hepatite B",
    },
  },
  {
    tuss: "40314103", // HCV-RNA PCR quantitativo (catálogo examesTUSS)
    formulario: {
      id: "cv-hcv",
      tipo: "cargaViral",
      nome: "Formulário de Carga Viral — Hepatite C",
      descricao:
        "Solicitação de Exame de Carga Viral do Vírus da Hepatite C (gov.br/aids)",
      codigoSUS: "02.02.03.108-0", // Quantificação de RNA do HCV
      agravoDoenca: "Hepatite C",
    },
  },
  {
    tuss: "40399039", // genérico (biologia molecular/PCR) — Delta não tem TUSS próprio
    formulario: {
      id: "cv-hdv",
      tipo: "cargaViral",
      nome: "Formulário de Carga Viral — Hepatite D (Delta)",
      descricao:
        "Solicitação de Exame de Carga Viral do Vírus da Hepatite D (Delta) (gov.br/aids)",
      // o formulário oficial da Delta não tem bloco de código de procedimento
      agravoDoenca: "Hepatite D (Delta)",
    },
  },
  {
    // O formulário é de DNA pró-viral (QUALITATIVO) → dispara no exame
    // qualitativo por PCR, não no de carga viral (quantitativo). (Leonardo, AI_96)
    tuss: "40314138", // HIV — qualitativo por PCR (catálogo examesTUSS)
    formulario: {
      id: "cv-hiv",
      tipo: "cargaViral",
      nome: "Formulário de Detecção do DNA pró-viral do HIV-1",
      descricao:
        "Solicitação de Exame de Detecção do DNA pró-viral do HIV-1 (gov.br/aids)",
      codigoSUS: "02.02.03.004-0", // Detecção de ácidos nucleicos do HIV-1 (qualitativo)
      agravoDoenca: "HIV",
    },
  },
  {
    tuss: "40314111", // HCV genotipagem (catálogo examesTUSS; cód. de Leonardo)
    formulario: {
      // tipo "cargaViral" reaproveita o motor de geração/modal (config geno-hcv)
      id: "geno-hcv",
      tipo: "cargaViral",
      nome: "Formulário de Genotipagem do VHC",
      descricao:
        "Solicitação de Exame de Genotipagem do Vírus da Hepatite C (gov.br/aids)",
      codigoSUS: "02.02.03.021-0", // Genotipagem de Vírus da Hepatite C
      agravoDoenca: "Hepatite C",
    },
  },
];

// ------------------------------------------------------------
// CONSULTA
// ------------------------------------------------------------
/**
 * Dado o(s) código(s) TUSS selecionado(s) num pedido, retorna os formulários
 * auxiliares que devem ser oferecidos. Deduplica por id de formulário (dois
 * exames podem apontar para o mesmo documento).
 */
// ------------------------------------------------------------
// APAC — imagem de média/alta complexidade
// ------------------------------------------------------------
// Diferente dos gatilhos 1:1 acima: VÁRIOS exames de imagem caem num ÚNICO
// laudo APAC (procedimento principal + secundários). Em vez de 1 linha por
// TUSS, mantemos o conjunto dos códigos que EXIGEM APAC e geramos a APAC uma
// vez se qualquer um deles estiver no pedido. Regra (Porto Velho, confirmada
// por Leonardo + pesquisa): TC, RM, cintilografia, densitometria óssea e CPRE
// exigem; US, doppler, FibroScan, endoscopia (exceto CPRE), radiografia e
// PET-CT NÃO (PET-CT e CPRE definidos por Leonardo, AI_96).
export const TUSS_EXIGE_APAC = new Set<string>([
  // Tomografia computadorizada (TC + angiotomografias)
  "41001010", "41001079", "41001109", "41001095", "41001117",
  "41001184", "41001176", "41001435", "41001443", "41001451", "41001516",
  // Ressonância magnética (RM + angio-RM + colangiorressonância)
  "41101170", "41101359", "41101189", "41101014", "41101120",
  "41101430", "41101340", "41101510", "41101529",
  // Medicina nuclear (cintilografias)
  "40702022", "40702030", "40704017", "40706010",
  // Densitometria óssea (laudo APAC próprio no SUS — confirmado por Leonardo)
  "40808130",
  // CPRE — endoscópica, mas caso especial de alto custo que exige APAC
  // (confirmado por Leonardo, AI_96).
  "40201074",
]);

export const FORM_APAC: FormularioAuxiliar = {
  id: "apac-imagem",
  tipo: "apac",
  nome: "Laudo APAC (imagem de média/alta complexidade)",
  descricao:
    "Laudo para Solicitação/Autorização de Procedimento Ambulatorial (APAC)",
};

/** Exame (por TUSS) exige laudo APAC? */
export function exigeAPAC(tuss: string): boolean {
  return TUSS_EXIGE_APAC.has(tuss);
}

// Código SIGTAP (campo 15 do laudo APAC = código do procedimento principal),
// indexado pelo TUSS do exame. ATENÇÃO: código SIGTAP errado causa glosa na
// autorização — por isso só entram aqui os CONFIRMADOS em fonte oficial; os
// demais ficam em branco até Leonardo fornecer/conferir (B139).
export const SIGTAP_POR_TUSS: Record<string, string> = {
  // Tomografia computadorizada
  "41001010": "02.06.01.007-9", // TC de crânio
  "41001079": "02.06.02.003-1", // TC de tórax
  "41001109": "02.06.03.001-0", // TC de abdome superior
  // TC de abdome total: SIGTAP não tem código específico — Leonardo orientou
  // usar o mesmo da TC de abdome (superior).
  "41001095": "02.06.03.001-0", // TC de abdome total (= abdome superior, decisão de Leonardo)
  // Ressonância magnética
  "41101014": "02.07.01.006-4", // RM de crânio (encéfalo)
  "41101120": "02.07.02.003-5", // RM de tórax
  "41101189": "02.07.03.002-2", // RM de pelve (bacia/pelve)
  "41101170": "02.07.03.001-4", // RM de abdome superior
  "41101359": "02.07.03.004-9", // Colangiorressonância (RM de vias biliares)
  // Medicina nuclear
  "40706010": "02.08.05.002-7", // Cintilografia óssea (corpo total/esqueleto)
  // Radiologia
  "40808130": "02.04.06.002-8", // Densitometria óssea (rotina coluna+fêmur)
  // Endoscopia de alto custo
  "40201074": "02.09.01.001-0", // CPRE — colangiopancreatografia retrógrada
};

/** Código SIGTAP do exame (campo 15 do APAC), ou undefined se não mapeado. */
export function sigtapPorTuss(tuss: string): string | undefined {
  return SIGTAP_POR_TUSS[tuss];
}

// ------------------------------------------------------------
// LME — Componente Especializado (CEAF / "alto custo")
// ------------------------------------------------------------
// Diferente dos gatilhos por TUSS (exame): o LME é disparado pelo RECEITUÁRIO,
// por MEDICAMENTO. A lista de medicamentos que exigem LME varia por estado;
// adotamos o núcleo do CEAF que usamos na prática: imunossupressores do
// transplante hepático + ácido ursodesoxicólico (decisão de Leonardo, AI_97) +
// estatinas/dislipidemia (B280, AI_120 — CIDs E78x).
// Cada item é o "nome-base" normalizado (sem acento/caixa); a detecção casa por
// inclusão, então "Tacrolimo 1mg cápsula" e "Tacrolimo XL" batem em "tacrolimo".
// `dislipidemia: true` marca os que sugerem os CIDs E78x no atalho de diagnóstico
// do LME (em vez dos CIDs de hepatologia/transplante). As bases das estatinas são
// os nomes completos ("sinvastatina"...), NUNCA o sufixo "statina" — senão
// "Nistatina" (antifúngico) cairia por engano.
export const MEDICAMENTOS_EXIGEM_LME: {
  base: string;
  rotulo: string;
  dislipidemia?: boolean;
}[] = [
  { base: "tacrolimo", rotulo: "Tacrolimo" },
  { base: "sirolimo", rotulo: "Sirolimo" },
  { base: "everolimo", rotulo: "Everolimo" },
  { base: "micofenolato", rotulo: "Micofenolato" },
  { base: "azatioprina", rotulo: "Azatioprina" },
  { base: "ciclosporina", rotulo: "Ciclosporina" },
  { base: "ursodesoxicolico", rotulo: "Ácido ursodesoxicólico" },
  // Estatinas / dislipidemia (B280, AI_120) — diagnóstico via CIDs E78x.
  { base: "sinvastatina", rotulo: "Sinvastatina", dislipidemia: true },
  { base: "atorvastatina", rotulo: "Atorvastatina", dislipidemia: true },
  { base: "rosuvastatina", rotulo: "Rosuvastatina", dislipidemia: true },
  { base: "pravastatina", rotulo: "Pravastatina", dislipidemia: true },
  { base: "fluvastatina", rotulo: "Fluvastatina", dislipidemia: true },
  { base: "lovastatina", rotulo: "Lovastatina", dislipidemia: true },
  { base: "pitavastatina", rotulo: "Pitavastatina", dislipidemia: true },
];

export const FORM_LME: FormularioAuxiliar = {
  id: "lme-ceaf",
  tipo: "lme",
  nome: "LME — Laudo do Componente Especializado (alto custo)",
  descricao:
    "Laudo de Solicitação, Avaliação e Autorização de Medicamento(s) — CEAF",
};

const normalizarMed = (s: string): string =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

/**
 * Dado o nome de um medicamento prescrito, retorna o nome-base do CEAF que o
 * dispara (ou undefined). Ex.: "Tacrolimo 1mg cápsula" → "tacrolimo".
 */
export function medicamentoExigeLME(nomeMedicamento: string): string | undefined {
  const norm = normalizarMed(nomeMedicamento);
  const achado = MEDICAMENTOS_EXIGEM_LME.find((m) => norm.includes(m.base));
  return achado?.base;
}

/** Algum medicamento da receita exige LME? */
export function exigeLME(nomesMedicamentos: string[]): boolean {
  return nomesMedicamentos.some((n) => !!medicamentoExigeLME(n));
}

/**
 * O medicamento do LME é de DISLIPIDEMIA (estatina etc.)? Usado pelo modal LME
 * para sugerir os CIDs E78x no atalho de diagnóstico, em vez dos de hepatologia.
 */
export function medicamentoLMEehDislipidemia(nomeMedicamento: string): boolean {
  const norm = normalizarMed(nomeMedicamento);
  const achado = MEDICAMENTOS_EXIGEM_LME.find((m) => norm.includes(m.base));
  return Boolean(achado?.dislipidemia);
}

// ------------------------------------------------------------
// HEPATITE B — Formulário de Solicitação de Medicamentos (PCDT)
// ------------------------------------------------------------
// Também disparado pelo RECEITUÁRIO, por MEDICAMENTO — mas é um formulário
// PRÓPRIO (não é o LME do CEAF). A própria ficha lista os medicamentos que a
// exigem (campo 34): Alfapeginterferona 2a, Entecavir, Tenofovir (TDF),
// Tenofovir Alafenamida (TAF) e Imunoglobulina humana anti-hepatite B.
// Cada item carrega o rótulo OFICIAL (campo 34) e a posologia padrão da ficha.
export interface MedHepatiteBCampo34 {
  id: string; // chave estável usada no estado/detecção
  rotulo: string; // rótulo oficial do campo 34
  posologia: string; // posologia padrão impressa na ficha
}

export const MEDICAMENTOS_HBV_CAMPO34: MedHepatiteBCampo34[] = [
  {
    id: "alfapeg",
    rotulo: "Alfapeginterferona 2a 180mcg",
    posologia: "Aplicar 180mcg/1,73m², por via subcutânea, uma vez por semana.",
  },
  {
    id: "entecavir",
    rotulo: "Entecavir 0,5mg",
    posologia: "Tomar _____ comprimido(s), por via oral, uma vez ao dia.",
  },
  {
    id: "tdf",
    rotulo: "Tenofovir (TDF) 300mg",
    posologia: "Tomar 1 (um) comprimido, por via oral, uma vez ao dia.",
  },
  {
    id: "taf",
    rotulo: "Tenofovir Alafenamida (TAF) 25mg",
    posologia: "Tomar 1 (um) comprimido, por via oral, uma vez ao dia.",
  },
  {
    id: "imunoglobulina",
    rotulo: "Imunoglobulina humana anti-hepatite B 1.000UI",
    posologia: "Administrar 1.000 UI, por via intramuscular, 1 (uma) vez ao mês.",
  },
];

export const FORM_HEPATITE_B: FormularioAuxiliar = {
  id: "hep-b",
  tipo: "hepatiteB",
  nome: "Formulário de Solicitação de Medicamentos — Hepatite B",
  descricao:
    "Formulário de Solicitação de Medicamentos – Hepatite B (PCDT, SETEMBRO/2023)",
};

/**
 * Dado o nome de um medicamento prescrito, retorna o id do medicamento do
 * campo 34 da ficha de Hepatite B que ele representa (ou undefined).
 * A ordem importa: TAF ("alafenamida"/"taf") é testado ANTES do TDF, pois
 * "Tenofovir alafenamida" também contém "tenofovir".
 */
export function medicamentoExigeHepatiteB(
  nomeMedicamento: string
): string | undefined {
  const n = normalizarMed(nomeMedicamento);
  if (n.includes("alfapeginterferona") || n.includes("peginterferona"))
    return "alfapeg";
  if (n.includes("entecavir")) return "entecavir";
  if (n.includes("alafenamida") || /\btaf\b/.test(n)) return "taf";
  if (n.includes("tenofovir")) return "tdf";
  if (n.includes("imunoglobulina") && n.includes("hepatite b"))
    return "imunoglobulina";
  return undefined;
}

/** Algum medicamento da receita exige o formulário de Hepatite B? */
export function exigeHepatiteB(nomesMedicamentos: string[]): boolean {
  return nomesMedicamentos.some((n) => !!medicamentoExigeHepatiteB(n));
}

export function formulariosParaExames(
  tussSelecionados: string[]
): FormularioAuxiliar[] {
  const vistos = new Set<string>();
  const resultado: FormularioAuxiliar[] = [];

  for (const gatilho of GATILHOS_FORMULARIOS) {
    if (
      tussSelecionados.includes(gatilho.tuss) &&
      !vistos.has(gatilho.formulario.id)
    ) {
      vistos.add(gatilho.formulario.id);
      resultado.push(gatilho.formulario);
    }
  }

  // APAC: uma vez, se qualquer exame de imagem do pedido exigir.
  if (tussSelecionados.some((t) => TUSS_EXIGE_APAC.has(t))) {
    resultado.push(FORM_APAC);
  }

  return resultado;
}
